import { apiBase as API_BASE } from './environment';
import React, { useCallback, useEffect, useState } from 'react';
import { completionMessage, formatEvidenceTime } from './complianceEvidence';



async function driverApi(user, path, options = {}) {
  if (!user) throw new Error('Sign in to continue.');
  const token = await user.getIdToken();
  const response = await fetch(API_BASE + path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
      ...(options.headers || {}),
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.ok === false) {
    const error = new Error(body.error || body.message || 'Request failed.');
    error.retryAt = body.retryAt;
    throw error;
  }
  return body;
}

function titleCase(value) {
  return String(value || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDate(value) {
  if (!value) return 'No date set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function assignmentTone(state) {
  if (state === 'completed' || state === 'waived') return 'complete';
  if (state === 'overdue' || state === 'expired') return 'danger';
  if (state === 'in_progress') return 'progress';
  return 'ready';
}

function StatusBadge({ state }) {
  return <span className={'compliance-status compliance-status-' + assignmentTone(state)}>{titleCase(state)}</span>;
}

function ProgressRing({ value }) {
  const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="compliance-progress-ring" style={{ '--compliance-progress': safeValue + '%' }}>
      <span><strong>{safeValue}%</strong><small>complete</small></span>
    </div>
  );
}

function AssignmentList({ assignments, selectedId, onSelect }) {
  if (!assignments.length) {
    return (
      <div className="compliance-empty">
        <strong>No training assignments</strong>
        <p>New required training will appear here as soon as ChemCab compliance releases it.</p>
      </div>
    );
  }
  return (
    <div className="compliance-assignment-list">
      {assignments.map((assignment) => (
        <button
          type="button"
          key={assignment.assignmentId}
          className={'compliance-assignment-row' + (selectedId === assignment.assignmentId ? ' is-selected' : '')}
          onClick={() => onSelect(assignment)}
        >
          <div>
            <span>{assignment.requiredForOnline ? 'Required' : 'Recommended'} · v{assignment.moduleVersion}</span>
            <strong>{assignment.title}</strong>
            <small>{assignment.dueAt ? 'Due ' + formatDate(assignment.dueAt) : 'No deadline'}</small>
          </div>
          <StatusBadge state={assignment.state} />
        </button>
      ))}
    </div>
  );
}

function LessonContent({ content }) {
  return (
    <div className="compliance-lesson-stack">
      {(content?.sections || []).map((section, index) => (
        <article className="compliance-lesson-section" id={section.id || 'lesson-' + index} key={section.id || index}>
          <span>Section {index + 1}</span>
          <h3>{section.heading}</h3>
          {section.body && <p>{section.body}</p>}
          {Array.isArray(section.bullets) && section.bullets.length > 0 && (
            <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>
          )}
          {section.imageUrl && (
            <figure>
              <img src={section.imageUrl} alt={section.imageAlt || section.heading || 'Training reference'} />
              {section.imageCaption && <figcaption>{section.imageCaption}</figcaption>}
            </figure>
          )}
        </article>
      ))}
    </div>
  );
}

function AssessmentWorkspace({
  content,
  assignment,
  attemptId,
  answers,
  setAnswers,
  acknowledgement,
  setAcknowledgement,
  result,
  busy,
  onSubmit,
  onReturn,
}) {
  const [questionIndex, setQuestionIndex] = useState(0);
  const questions = content?.questions || [];
  const question = questions[questionIndex] || null;
  const answeredCount = questions.filter((item) => answers[item.id]).length;

  if (result) {
    return (
      <section className={'compliance-result-card ' + (result.passed ? 'is-pass' : 'is-review')}>
        <span className="compliance-result-icon">{result.passed ? '✓' : '↻'}</span>
        <p className="eyebrow">{result.passed ? 'Module complete' : 'Review and retry'}</p>
        <h2>{result.passed ? 'You passed with ' + result.score + '%' : 'You scored ' + result.score + '%'}</h2>
        <p>
          {result.passed
            ? 'Your certificate and online eligibility were updated immediately.'
            : 'Your assignment remains open. Review the guidance below before starting a fresh attempt.'}
        </p>
        {!result.passed && Array.isArray(result.remediation) && result.remediation.length > 0 && (
          <div className="compliance-remediation">
            {result.remediation.map((item) => (
              <article key={item.questionId}>
                <strong>Review needed</strong>
                <p>{item.explanation || 'Revisit the related lesson section before trying again.'}</p>
                {item.remediationSectionId && <a href={'#' + item.remediationSectionId}>Open related section</a>}
              </article>
            ))}
          </div>
        )}
        <button className="primary-button" type="button" onClick={onReturn}>
          {result.passed ? 'Return to task list' : 'Review lesson'}
        </button>
      </section>
    );
  }

  if (!attemptId) return null;

  return (
    <section className="compliance-assessment">
      <header>
        <div>
          <p className="eyebrow">Server-graded assessment</p>
          <h2>{assignment.title}</h2>
        </div>
        <span>{answeredCount}/{questions.length} answered</span>
      </header>
      {questions.length > 0 && (
        <>
          <div className="compliance-question-progress">
            {questions.map((item, index) => (
              <button
                key={item.id}
                type="button"
                className={(index === questionIndex ? 'is-current ' : '') + (answers[item.id] ? 'is-answered' : '')}
                onClick={() => setQuestionIndex(index)}
                aria-label={'Question ' + (index + 1)}
              />
            ))}
          </div>
          <article className="compliance-question-card">
            <span>Question {questionIndex + 1} of {questions.length}</span>
            <h3>{question?.prompt}</h3>
            <div className="compliance-answer-list">
              {(question?.options || []).map((option, optionIndex) => (
                <button
                  key={option.id}
                  type="button"
                  className={answers[question.id] === option.id ? 'is-selected' : ''}
                  onClick={() => setAnswers((current) => ({ ...current, [question.id]: option.id }))}
                >
                  <b>{String.fromCharCode(65 + optionIndex)}</b>
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </article>
        </>
      )}
      {content.acknowledgementRequired && (
        <label className="compliance-acknowledgement">
          <input type="checkbox" checked={acknowledgement} onChange={(event) => setAcknowledgement(event.target.checked)} />
          <span>I acknowledge that I understand this policy and will follow it while performing ChemCab work.</span>
        </label>
      )}
      <footer className="compliance-assessment-footer">
        {questions.length > 0 && (
          <button type="button" className="ghost-button" disabled={questionIndex === 0} onClick={() => setQuestionIndex((value) => Math.max(0, value - 1))}>Previous</button>
        )}
        {questions.length > 0 && questionIndex < questions.length - 1 ? (
          <button type="button" className="primary-button" onClick={() => setQuestionIndex((value) => Math.min(questions.length - 1, value + 1))}>Next question</button>
        ) : (
          <button
            type="button"
            className="primary-button"
            disabled={busy || answeredCount !== questions.length || (content.acknowledgementRequired && !acknowledgement)}
            onClick={onSubmit}
          >
            {busy ? 'Recording securely…' : questions.length ? 'Submit assessment' : 'Confirm acknowledgement'}
          </button>
        )}
      </footer>
    </section>
  );
}

export function DriverPerformancePanel({ portalUser }) {
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    driverApi(portalUser, '/api/driver/performance?limit=30')
      .then((body) => {
        if (active) setPerformance(body.performance);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [portalUser]);

  if (loading) return <div className="compliance-loading"><span /><strong>Loading performance…</strong></div>;
  if (error) return <div className="form-error">{error}</div>;

  const average = performance?.averageRating;
  return (
    <div className="performance-workspace">
      <div className="performance-hero">
        <div>
          <p className="eyebrow">Client feedback</p>
          <h1>Your delivery performance.</h1>
          <p>Ratings come from one-time delivery-completion links and cannot be submitted twice.</p>
        </div>
        <div className="performance-score">
          <strong>{average ? Number(average).toFixed(2) : '—'}</strong>
          <div aria-label={average ? average + ' out of 5 stars' : 'No ratings yet'}>
            {[1, 2, 3, 4, 5].map((value) => <span key={value} className={average >= value - 0.5 ? 'is-active' : ''}>★</span>)}
          </div>
          <small>{Number(performance?.ratingCount || 0)} verified rating{Number(performance?.ratingCount || 0) === 1 ? '' : 's'}</small>
        </div>
      </div>
      <section className="portal-panel performance-panel">
        <div className="performance-panel-head">
          <div><p className="eyebrow">Recent deliveries</p><h2>What clients shared</h2></div>
          <span>Comments are optional</span>
        </div>
        <div className="performance-feedback-list">
          {(performance?.recentFeedback || []).map((item) => (
            <article key={item.feedbackId}>
              <div className="performance-feedback-stars">{[1, 2, 3, 4, 5].map((value) => <span key={value} className={value <= item.rating ? 'is-active' : ''}>★</span>)}</div>
              <strong>{item.comment || 'Rating submitted without a comment.'}</strong>
              <small>{formatDate(item.submittedAt)}{item.referenceNumbers?.length ? ' · ' + item.referenceNumbers.join(', ') : ''}</small>
            </article>
          ))}
          {!performance?.recentFeedback?.length && (
            <div className="compliance-empty"><strong>No ratings yet</strong><p>Your verified client feedback will appear here after completed deliveries.</p></div>
          )}
        </div>
      </section>
    </div>
  );
}

export default function ComplianceWorkspace({
  portalUser,
  LegacyDashboardComponent = null,
}) {
  const [view, setView] = useState('tasks');
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [content, setContent] = useState(null);
  const [attemptId, setAttemptId] = useState('');
  const [answers, setAnswers] = useState({});
  const [acknowledgement, setAcknowledgement] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const openAssignment = useCallback(async (assignment) => {
    setBusy(true);
    setError('');
    setSelectedAssignment(assignment);
    setAttemptId('');
    setAnswers({});
    setAcknowledgement(false);
    setResult(null);
    try {
      const body = await driverApi(
        portalUser,
        '/api/driver/compliance/assignments/' + encodeURIComponent(assignment.assignmentId) + '/content'
      );
      setContent(body.content);
      setSelectedAssignment(body.assignment || assignment);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }, [portalUser]);

  const loadHome = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const body = await driverApi(portalUser, '/api/driver/compliance/home');
      setHome(body);
      const requestedAssignment = new URLSearchParams(window.location.search).get('assignment');
      const requestedDocument = new URLSearchParams(window.location.search).get('document');
      if (requestedDocument) setView('documents');
      if (requestedAssignment) {
        const assignment = (body.assignments || []).find((item) => item.assignmentId === requestedAssignment);
        if (assignment) {
          setView('tasks');
          await openAssignment(assignment);
        }
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [portalUser, openAssignment]);

  useEffect(() => {
    void loadHome();
  }, [loadHome]);

  async function startAttempt() {
    if (!selectedAssignment) return;
    setBusy(true);
    setError('');
    try {
      const body = await driverApi(
        portalUser,
        '/api/driver/compliance/assignments/' + encodeURIComponent(selectedAssignment.assignmentId) + '/attempts',
        { method: 'POST', body: '{}' }
      );
      setAttemptId(body.attemptId);
      setContent(body.content);
      setAnswers({});
      setAcknowledgement(false);
      setResult(null);
    } catch (requestError) {
      setError(
        requestError.retryAt
          ? 'Your next attempt is available ' + formatDate(requestError.retryAt) + '.'
          : requestError.message
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitAttempt() {
    setBusy(true);
    setError('');
    try {
      const body = await driverApi(
        portalUser,
        '/api/driver/compliance/attempts/' + encodeURIComponent(attemptId) + '/submit',
        {
          method: 'POST',
          body: JSON.stringify({ answers, acknowledgement }),
        }
      );
      setResult(body);
      const refreshed = await driverApi(portalUser, '/api/driver/compliance/home');
      setHome(refreshed);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  const assignments = home?.assignments || [];
  const activeAssignments = assignments.filter((assignment) => !['completed', 'waived'].includes(assignment.state));
  const completedAssignments = assignments.filter((assignment) => ['completed', 'waived'].includes(assignment.state));
  // The backend controls rollback of legacy collection; missing policy stays closed.
  const legacyCollectionEnabled = home?.onboardingProvider?.legacyCollectionEnabled === true;
  const documents = home?.documents || [];
  const blockingDocuments = documents.filter((documentItem) => documentItem.status !== 'verified');
  const progress = home?.projection?.progressPercent || 0;
  const onlineEligible = home?.projection?.onlineEligible === true;
  const firstName = String(home?.driver?.name || portalUser?.displayName || 'Driver').split(' ')[0];
  const taskCount = activeAssignments.length + blockingDocuments.length;

  if (loading) return <div className="compliance-loading"><span /><strong>Building your compliance workspace…</strong></div>;
  if (!home) return <div className="form-error">{error || 'Compliance workspace unavailable.'}</div>;

  if (attemptId && content && selectedAssignment) {
    return (
      <div className="modern-compliance-workspace">
        {error && <div className="form-error">{error}</div>}
        <AssessmentWorkspace
          content={content}
          assignment={selectedAssignment}
          attemptId={attemptId}
          answers={answers}
          setAnswers={setAnswers}
          acknowledgement={acknowledgement}
          setAcknowledgement={setAcknowledgement}
          result={result}
          busy={busy}
          onSubmit={() => void submitAttempt()}
          onReturn={() => {
            setAttemptId('');
            setResult(null);
            setAnswers({});
          }}
        />
        {!result?.passed && <LessonContent content={content} />}
      </div>
    );
  }

  return (
    <div className="modern-compliance-workspace">
      <section className="compliance-hero">
        <div>
          <p className="eyebrow">Onboarding & compliance</p>
          <h1>{firstName}, your next step is clear.</h1>
          <p>
            {onlineEligible
              ? 'You are current and eligible to go online. We will surface future renewals here.'
              : taskCount + ' item' + (taskCount === 1 ? '' : 's') + ' need attention before your compliance record is current.'}
          </p>
          <div className="compliance-hero-badges">
            <span className={onlineEligible ? 'is-ready' : 'is-blocked'}>{onlineEligible ? 'Eligible to go online' : 'Online eligibility blocked'}</span>
            {Number(home?.projection?.overdueCount || 0) > 0 && <span className="is-overdue">{home.projection.overdueCount} overdue</span>}
          </div>
        </div>
        <ProgressRing value={progress} />
      </section>

      <nav className="compliance-view-tabs" aria-label="Compliance workspace">
        <button className={view === 'tasks' ? 'is-active' : ''} onClick={() => setView('tasks')}>Tasks <span>{activeAssignments.length}</span></button>
        <button className={view === 'documents' ? 'is-active' : ''} onClick={() => setView('documents')}>Documents <span>{blockingDocuments.length}</span></button>
        <button className={view === 'certificates' ? 'is-active' : ''} onClick={() => setView('certificates')}>Certificates <span>{home.certificates?.length || 0}</span></button>
        <button onClick={() => {
          window.history.pushState({}, '', '/performance');
          window.dispatchEvent(new PopStateEvent('popstate'));
        }}>Performance <span>★</span></button>
      </nav>

      {error && <div className="form-error">{error}</div>}

      {view === 'tasks' && (
        <div className="compliance-task-layout">
          <section className="compliance-task-panel">
            <header>
              <div><p className="eyebrow">Assigned to you</p><h2>Required training</h2></div>
              <button className="text-button" type="button" onClick={() => void loadHome()}>Refresh</button>
            </header>
            <AssignmentList assignments={[...activeAssignments, ...completedAssignments]} selectedId={selectedAssignment?.assignmentId} onSelect={(assignment) => void openAssignment(assignment)} />
          </section>
          <section className="compliance-detail-panel">
            {!selectedAssignment ? (
              <div className="compliance-detail-placeholder">
                <span>01</span>
                <h2>Select a module.</h2>
                <p>Read the assignment, complete its lesson, and start the assessment when ready.</p>
              </div>
            ) : busy && !content ? (
              <div className="compliance-loading"><span /><strong>Opening module…</strong></div>
            ) : (
              <>
                <header className="compliance-module-head">
                  <div>
                    <p className="eyebrow">{content?.category || 'Training'} · v{selectedAssignment.moduleVersion}</p>
                    <h2>{content?.title || selectedAssignment.title}</h2>
                    <p>{content?.subtitle || content?.summary || selectedAssignment.reason}</p>
                  </div>
                  <StatusBadge state={selectedAssignment.state} />
                </header>
                <div className="compliance-module-meta">
                  <span><small>Estimated time</small><strong>{content?.estimatedMinutes || 10} minutes</strong></span>
                  <span><small>Passing score</small><strong>{content?.passingScore || 100}%</strong></span>
                  <span><small>Due</small><strong>{formatDate(selectedAssignment.dueAt)}</strong></span>
                  <span><small>Recorded attempts</small><strong>{selectedAssignment.attemptCount ?? 'Not recorded'}</strong></span>
                  <span><small>Completed</small><strong>{formatEvidenceTime(selectedAssignment.completedAt)}</strong></span>
                  <span><small>Completion source</small><strong>{selectedAssignment.completionSource === 'legacy_import' ? 'Imported completion' : selectedAssignment.completionSource === 'assessment' ? 'Server-graded assessment' : 'Not recorded'}</strong></span>
                  {selectedAssignment.importedAt && <span><small>Imported (not completed)</small><strong>{formatEvidenceTime(selectedAssignment.importedAt)}</strong></span>}
                </div>
                <LessonContent content={content} />
                <div className="compliance-module-action">
                  <div>
                    <strong>{selectedAssignment.state === 'completed' ? 'Training complete' : 'Ready for the assessment?'}</strong>
                    <p>{completionMessage(selectedAssignment, home.certificates || [])}</p>
                  </div>
                  {selectedAssignment.state !== 'completed' && selectedAssignment.state !== 'waived' && (
                    <button className="primary-button" type="button" disabled={busy} onClick={() => void startAttempt()}>
                      {busy ? 'Starting…' : selectedAssignment.attemptCount ? 'Start next attempt' : 'Start assessment'}
                    </button>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {view === 'documents' && (
        <section className="compliance-documents-shell">
          <div className="compliance-section-heading">
            <div><p className="eyebrow">Document center</p><h2>{legacyCollectionEnabled ? 'Upload once. Track review here.' : 'Onboarding through Openforce'}</h2></div>
            <p>{legacyCollectionEnabled ? 'Missing, rejected, and expiring documents feed the same eligibility record used by the Driver App.' : 'ChemCab document uploads are paused. Follow the onboarding instructions from ChemCab; your required training remains available in Tasks.'}</p>
          </div>
          {/* Legacy upload/profile workspace retained for an explicit rollback. */}
          {legacyCollectionEnabled && LegacyDashboardComponent
            ? <LegacyDashboardComponent portalUser={portalUser} embedded documentsOnly />
            : <div className="compliance-empty"><strong>Documents and approval are handled through Openforce</strong><p>After ChemCab manually approves your Openforce onboarding, ChemCab will invite you to complete portal training. Existing records do not establish Openforce approval.</p></div>}
        </section>
      )}

      {view === 'certificates' && (
        <section>
          <div className="compliance-section-heading">
            <div><p className="eyebrow">Verified record</p><h2>Certificates</h2></div>
            <p>Each certificate is tied to the exact immutable module revision you completed.</p>
          </div>
          <div className="modern-certificate-grid">
            {(home.certificates || []).map((certificate) => (
              <article key={certificate.certificateId}>
                <span>Certified</span>
                <h3>{certificate.title}</h3>
                <code>{certificate.certificateId}</code>
                <dl>
                  <div><dt>Issued</dt><dd>{formatEvidenceTime(certificate.issuedAt)}</dd></div>
                  <div><dt>Valid until</dt><dd>{certificate.expiresAt ? formatDate(certificate.expiresAt) : 'No expiration'}</dd></div>
                </dl>
              </article>
            ))}
            {!home.certificates?.length && <div className="compliance-empty"><strong>No certificates yet</strong><p>New passing assessments issue a certificate. Imported completions may have no certificate in this workspace.</p></div>}
          </div>
        </section>
      )}
    </div>
  );
}
